import { useModalContext } from 'common/Context/ModalContext';
import React, { useRef } from 'react';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import type { BoostLowCallToActionProps } from '../../../types';
import styles from './BoostLowCallToAction.module.scss';

enum PartnerPromotionUrls {
  UberEats = 'https://help.uber.com/en-AU/merchants-and-restaurants/article/managing-campaigns-and-offers-through-uber-eats-manager?nodeId=f219d6d1-3ffa-4a3c-b223-af08e4691df6',
  DoorDash = 'https://merchants.doordash.com/en-au/learning-center/marketing-to-new-customers',
  Menulog = 'https://partneressentials.menulog.com.au/partner-essentials-post/promotional-tools-for-menulog-partners/'
}

interface InstructionProps {
  image: { src: string; alt: string };
  partnerPromotion: { url: string; text: string };
  appName: string;
  posName: string;
}

const Instruction: React.FC<InstructionProps> = ({ image, partnerPromotion, appName, posName }) => {
  const handleClick = (): void => {
    logAmplitudeEvent({
      name: 'click app setup offer',
      category: AmplitudeEventCategories.BOOST,
      properties: {
        [AmplitudeEventProperties.APP]: appName,
        [AmplitudeEventProperties.POS_NAME]: posName
      }
    });
  };

  return (
    <div className={styles.Instruction}>
      <div className={styles.InstructionText}>
        <img src={image.src} alt={image.alt} />
        <a href={partnerPromotion.url} target="_blank" rel="noreferrer" onClick={handleClick}>
          {partnerPromotion.text}
        </a>
      </div>
    </div>
  );
};

const BoostLowCallToAction: React.FC<BoostLowCallToActionProps> = ({ posName }) => {
  const { closeModal } = useModalContext();

  const closeRef = useRef<HTMLButtonElement>(null);

  return (
    <div className={styles.CallToActionContainer}>
      <div className={styles.Intro}>
        <h3>How to set up a promotion for your delivery apps</h3>
      </div>
      <div className={styles.Description}>
        <span>
          Here are some links from our partners you might find helpful to guide you through promotion and offer setup
          process
        </span>
      </div>
      <div className={styles.CornerContainer}>
        <button
          className={styles.CloseButton}
          type="button"
          aria-label="Close Modal"
          data-testid="corner-close-button"
          onClick={closeModal}
          ref={closeRef}
          tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        >
          <i className="mm-times" />
        </button>
      </div>
      <div>
        <Instruction
          image={{ src: '/Images/logo-ubereats.svg', alt: 'ubereats logo' }}
          partnerPromotion={{
            url: PartnerPromotionUrls.UberEats,
            text: 'Managing campaigns and offers through Uber Eats Manager'
          }}
          appName="ubereats"
          posName={posName}
        />
        <Instruction
          image={{ src: '/Images/logo-doordash.svg', alt: 'doordash logo' }}
          partnerPromotion={{
            url: PartnerPromotionUrls.DoorDash,
            text: 'How to Get the Most Out of DoorDash Ads & Promotions'
          }}
          appName="doordash"
          posName={posName}
        />
        <Instruction
          image={{ src: '/Images/logo-menulog.svg', alt: 'menulog logo' }}
          partnerPromotion={{
            url: PartnerPromotionUrls.Menulog,
            text: 'Promotional tools for Menulog partners'
          }}
          appName="menulog"
          posName={posName}
        />
      </div>
      <div className={styles.Footer}>
        <span>
          Interested in seeing promotion management in Doshii?&nbsp;
          <a
            href="https://portal.productboard.com/brxcizuig4cocphxrh3sep8v/c/23-boost-boost-low-periods"
            target="_blank"
            onClick={() => {
              logAmplitudeEvent({
                name: 'click productboard vote here',
                category: AmplitudeEventCategories.BOOST,
                properties: {
                  [AmplitudeEventProperties.SOURCE]: 'boost low offers'
                }
              });
            }}
            rel="noreferrer"
          >
            Vote here
          </a>
        </span>
      </div>
    </div>
  );
};

export default BoostLowCallToAction;
